import { applyMiddleware, createStore, Store, Middleware } from 'redux'
import { Action } from 'actions'
import { State } from 'interfaces/state'
import { createEpicMiddleware, Epic } from 'redux-observable'
import { get, post, patch, deleteAjax, sendBeacon } from 'helpers/observableAjaxHelpers'
import epics, { onResetState } from 'epics'
import reducers from 'reducers'
import { EpicDependencies } from 'interfaces/middleware'
import { remote } from 'helpers/mobileAppHelpers'
import { defaultMemoize } from 'reselect'
import { composeWithDevTools as composeWithRemoteDevTools } from 'remote-redux-devtools'
import { composeWithDevTools } from '@redux-devtools/extension'
import { isFeatureEnabled } from 'helpers/featureHelpers'
import { consoleRelay } from 'components/features/webSocketLogger/ConsoleWSRelay'

interface ImportWithDefault {
  'default': any
}

const getEpicMiddleware = defaultMemoize(() => createEpicMiddleware<Action, State, EpicDependencies>(
  epics as Epic<Action, State, EpicDependencies>,
  {
    dependencies: {
      document,
      navigator,
      get,
      post,
      sendBeacon,
      patch,
      deleteAjax,
      remote,
    },
  }
))

declare const __REDUX_REMOTE_HOST__: string | undefined
declare const __REDUX_REMOTE_PORT__: number | undefined

const actionSanitizer = (action: any) => {
  if (action.type === 'AGORA_CLIENT_EVENT' || action.type === 'AGORA_STREAM_EVENT') {
    const { payload, ...restAction } = action
    const { stream, ...restPayload } = payload
    return {
      ...restAction,
      type: `${ action.type }:${ action.payload.type }`,
      payload: {
        ...restPayload,
        ...stream ? { stream: { id: stream.getId() }} : {},
      },
    }
  }
  if (action.type === 'AGORA_SCREEN_SHARE_STREAM') {
    const { payload, ...restAction } = action
    const { params, ...restPayload } = payload
    const { stream, ...restParams } = params

    return {
      ...restAction,
      payload: {
        ...restPayload,
        params: {
          ...restParams,
          ...stream ? { stream: { id: stream.getId() }} : {},
        },
      },
    }
  }
  if (action.meta && typeof action.meta === 'string') {
    return {
      ...action,
      type: `${ action.type }/${ action.meta }`,
    }
  }

  return action
}

const getComposeEnhancers = () => {
  if (isFeatureEnabled('redux_remote') && __REDUX_REMOTE_HOST__ && __REDUX_REMOTE_PORT__) {
    return composeWithRemoteDevTools({
      realtime: true,
      port: __REDUX_REMOTE_PORT__,
      hostname: __REDUX_REMOTE_HOST__,
    })
  }

  return composeWithDevTools({
    actionSanitizer,
    serialize: {
      function: function (fn: () => void) { return fn.toString() },
    },
  })
}

const logActionsMiddleware: Middleware = () => (next) => (rawAction) => {
  const isActionsLogEnabled = isFeatureEnabled('actions')
  const isConsoleWSRelayEnabled = consoleRelay.isStarted

  const shouldLog = isActionsLogEnabled || isConsoleWSRelayEnabled || DEBUG_ACTIONS
  if (!shouldLog) {
    return next(rawAction)
  }

  const action = actionSanitizer(rawAction)

  if (isFeatureEnabled('actions') || isConsoleWSRelayEnabled || DEBUG_ACTIONS) {
    console.log('ACTION:', action.type, (action as any).meta || '', (action as any).payload?.type || '', action)
  }

  return next(rawAction)
}

// FOR DEBUG
const DEBUG_ACTIONS = false
const TRACED_ACTIONS: string[] = ['ACTIVATE_ANCHOR']
const traceActionsMiddleware: Middleware = () => {
  return (next) => (action) => {
    if (TRACED_ACTIONS.includes(action.type)) {
      console.trace(action.type, action)
    }

    return next(action)
  }
}

export const getStore = defaultMemoize((): Store<State> => {
  const store = getComposeEnhancers()(
    DEBUG_ACTIONS
      ? applyMiddleware(
        logActionsMiddleware,
        getEpicMiddleware(),
        traceActionsMiddleware
      )
      : applyMiddleware(
        logActionsMiddleware,
        getEpicMiddleware(),
      )
  )(createStore)(reducers)

  onResetState(getEpicMiddleware().replaceEpic, store.dispatch)

  return store
})


if (module.hot) {
  // Enable Webpack hot module replacement for reducers
  module.hot.accept('reducers/index', () => {
    // tslint:disable-next-line:no-require-imports
    const nextReducers = (require('reducers') as ImportWithDefault).default

    getStore().replaceReducer(nextReducers)
  })

  module.hot.accept('epics/index', () => {
    // tslint:disable-next-line:no-require-imports
    const nextEpics = (require('epics') as ImportWithDefault).default

    getEpicMiddleware().replaceEpic(nextEpics)
  })
}

